import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Moon, Sun, Menu } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';

const services = [
  { name: 'Création de site internet', href: '/services/site-internet' },
  { name: "Développement d'applications", href: '/services/applications' },
  { name: 'E-commerce', href: '/services/e-commerce' },
  { name: 'SEO / Référencement', href: '/services/seo' },
  { name: 'Maintenance', href: '/services/maintenance' },
  { name: 'Branding & Identité', href: '/services/branding' },
];

const navigation = [
  { name: 'Accueil', href: '/' },
  { name: 'À propos', href: '/a-propos' },
  { name: 'Services', href: '/services', hasDropdown: true },
  { name: 'Réalisations', href: '/realisations' },
  { name: 'Tarifs', href: '/tarifs' },
  { name: 'Blog', href: '/blog' },
  { name: 'Contact', href: '/contact' },
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const isDarkMode = document.documentElement.classList.contains('dark');
    setIsDark(isDarkMode);
  }, []);

  const toggleTheme = () => {
    document.documentElement.classList.toggle('dark');
    setIsDark((prev) => !prev);
  };

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
          ? 'bg-background/80 backdrop-blur-xl border-b border-border shadow-sm'
          : 'bg-transparent'
        }`}
    >
      <nav className="weblax-container" aria-label="Navigation principale">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link
            to="/"
            data-testid="logo-link"
            className="flex items-center gap-2 group"
            aria-label="Retour à l’accueil Weblax"
          >
            <img
              src="/images/logo-weblax.png"
              alt="Weblax - Agence web et SEO"
              className="w-10 h-10 object-contain transition-transform group-hover:scale-105"
            />
            <span className="font-bold text-xl text-foreground">Weblax</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navigation.map((item) =>
              item.hasDropdown ? (
                <DropdownMenu key={item.name}>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      data-testid={`nav-${item.name.toLowerCase()}`}
                      className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isActive(item.href)
                          ? 'text-primary bg-primary/10'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                        }`}
                      aria-label="Ouvrir le menu Services"
                    >
                      {item.name}
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </DropdownMenuTrigger>

                  <DropdownMenuContent align="start" className="w-64">
                    <DropdownMenuItem asChild>
                      <Link to="/services" className="w-full font-medium">
                        Tous nos services
                      </Link>
                    </DropdownMenuItem>

                    {services.map((service) => (
                      <DropdownMenuItem key={service.href} asChild>
                        <Link to={service.href} className="w-full">
                          {service.name}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s/g, '-')}`}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isActive(item.href)
                      ? 'text-primary bg-primary/10'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                >
                  {item.name}
                </Link>
              )
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle"
              className="rounded-lg"
              aria-label={isDark ? 'Activer le mode clair' : 'Activer le mode sombre'}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>

            <Link to="/devis" className="hidden sm:block">
              <Button data-testid="cta-devis" className="weblax-btn-primary">
                Demander un devis
              </Button>
            </Link>

            {/* Mobile Menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  data-testid="mobile-menu-btn"
                  className="lg:hidden"
                  aria-label="Ouvrir le menu mobile"
                >
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>

              <SheetContent side="right" className="w-80">
                <div className="flex flex-col gap-4 mt-8">
                  {navigation.map((item) => (
                    <div key={item.name}>
                      <Link
                        to={item.href}
                        onClick={() => setMobileOpen(false)}
                        className={`block px-4 py-3 rounded-lg font-medium transition-colors ${isActive(item.href)
                            ? 'text-primary bg-primary/10'
                            : 'text-foreground hover:bg-muted'
                          }`}
                      >
                        {item.name}
                      </Link>

                      {item.hasDropdown && (
                        <div className="ml-4 mt-2 space-y-1">
                          <Link
                            to="/services"
                            onClick={() => setMobileOpen(false)}
                            className="block px-4 py-2 text-sm font-medium text-foreground hover:text-primary"
                          >
                            Tous nos services
                          </Link>

                          {services.map((service) => (
                            <Link
                              key={service.href}
                              to={service.href}
                              onClick={() => setMobileOpen(false)}
                              className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground"
                            >
                              {service.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}

                  <div className="pt-4 border-t border-border">
                    <Link to="/devis" onClick={() => setMobileOpen(false)}>
                      <Button className="weblax-btn-primary w-full">
                        Demander un devis
                      </Button>
                    </Link>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;